import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Briefcase, Truck, Zap, Car, ShieldCheck, KeyRound, Sparkles, Lock,
  Clock, Timer, LifeBuoy, Rocket, Building2, ArrowRight, BadgeCheck,
  MapPin, PhoneCall, Wallet, CalendarClock, Users2, Wrench,
  TrendingUp, HeartHandshake, Layers, LineChart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/context/AuthContext";

// ---- helpers -----------------------------------------------------------------

function pickBy(vehicles, categories) {
  return vehicles.filter((v) => categories.includes(v.category));
}

// Ensure the marquee has enough tiles to render a seamless loop even with a small pool.
function padTiles(list, min = 8) {
  if (!list || list.length === 0) return [];
  const out = [];
  let i = 0;
  while (out.length < min) {
    out.push({ ...list[i % list.length], _key: `${list[i % list.length].id}-${out.length}` });
    i += 1;
  }
  return out;
}

// Detect vehicles with truly transparent background images (from Curbo S3).
function isTransparent(url) {
  return typeof url === "string" && (url.includes("curbo.s3.amazonaws.com") || url.includes("_remove_bg") || url.includes("/brand/alfadrive-lion-transparent"));
}

// ---- marquee-item card -------------------------------------------------------

const MarqueeTile = ({ vehicle }) => {
  const trans = isTransparent(vehicle.image_url);
  return (
    <Link
      to={`/vehicles/${vehicle.id}`}
      className={`mx-3 group relative inline-flex flex-col items-center justify-end rounded-3xl border p-4 hover:shadow-[0_18px_45px_-18px_hsla(220,90%,45%,0.35)] transition-all shine-on-hover ${
        trans
          ? "bg-white/70 border-border hover:border-[hsl(var(--blue-300))]"
          : "bg-gradient-to-br from-white to-[hsl(var(--charcoal-100))] border-border hover:border-[hsl(var(--blue-300))]"
      }`}
      style={{ width: 280, minWidth: 280 }}
      data-testid={`op-marquee-tile-${vehicle.id}`}
    >
      <div className="relative h-32 w-full flex items-center justify-center overflow-hidden rounded-2xl">
        {!trans && (
          <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,hsla(220,40%,90%,0.55)_100%)]" />
        )}
        <img
          src={vehicle.image_url}
          alt={vehicle.name}
          className={`transition-transform duration-500 group-hover:-translate-y-1 ${
            trans
              ? "max-h-32 max-w-full object-contain drop-shadow-[0_18px_18px_rgba(15,23,42,0.14)]"
              : "h-full w-full object-cover"
          }`}
          loading="lazy"
          onError={(e) => { e.currentTarget.src = "/brand/alfadrive-lion-transparent.png"; e.currentTarget.style.opacity = "0.55"; e.currentTarget.className = "max-h-28 object-contain"; }}
        />
        {trans && (
          <div aria-hidden className="absolute inset-x-8 bottom-1 h-3 rounded-full blur-md bg-[hsl(var(--charcoal-900))]/12" />
        )}
      </div>
      <div className="mt-3 w-full text-center">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{vehicle.brand}</div>
        <div className="font-display text-sm font-semibold text-foreground leading-tight truncate">
          {(() => {
            const b = (vehicle.brand || "").trim();
            const n = (vehicle.name || "").trim();
            // Drop leading brand from the name when present so we don't render "Ford Mustang Ford Mustang GT".
            if (b && n.toLowerCase().startsWith(b.toLowerCase())) {
              const stripped = n.slice(b.length).trim();
              return stripped || n;
            }
            return n;
          })()}
        </div>
      </div>
    </Link>
  );
};

const Marquee = ({ items, speed = "" }) => {
  if (!items || items.length === 0) return null;
  const doubled = [...items, ...items];
  return (
    <div className="marquee py-2">
      <div className={`marquee-track ${speed}`}>
        {doubled.map((v, i) => (
          <MarqueeTile key={v._key + "-" + i} vehicle={v} />
        ))}
      </div>
    </div>
  );
};

// ---- page --------------------------------------------------------------------

export default function AlfaOnePathPage() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ business_name: "", business_type: "sole_prop", business_purpose: "", vehicle_need: "", estimated_duration: "3 months" });
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/vehicles?limit=100`);
        setVehicles(res.data || []);
      } catch {
        setVehicles([]);
      }
    })();
  }, []);

  // Sort transparents first so the visually cleaner tiles lead the marquee.
  const byTransparency = (a, b) => (isTransparent(a.image_url) ? -1 : 0) - (isTransparent(b.image_url) ? -1 : 0);

  const MIN_ITEMS = 4;
  const expandIfSparse = (primary, fallback) => {
    if (primary.length >= MIN_ITEMS) return primary;
    const seen = new Set(primary.map((v) => v.id));
    const extra = fallback.filter((v) => !seen.has(v.id));
    return [...primary, ...extra].slice(0, 10);
  };

  const trucks = useMemo(() => {
    const primary = pickBy(vehicles, ["truck"]);
    // Contractor-friendly fallback: SUVs are the next-best "carry stuff" vehicle.
    const list = expandIfSparse(primary, pickBy(vehicles, ["luxury_suv"]));
    return padTiles(list.sort(byTransparency));
  }, [vehicles]);

  // Economy & rideshare — strictly practical daily-use vehicles.
  const economy = useMemo(() => {
    const primary = pickBy(vehicles, ["economy", "rideshare"]);
    // Broad fallback to daily-driver sedans if we don't have dedicated economy stock.
    const list = expandIfSparse(primary, pickBy(vehicles, ["business", "premium_sedan"]));
    return padTiles(list.sort(byTransparency));
  }, [vehicles]);

  // Electric — Tesla, Electric, or Hybrid fuel types.
  const electric = useMemo(() => {
    const list = vehicles.filter((v) => {
      const f = (v.fuel_type || "").toLowerCase();
      const b = (v.brand || "").toLowerCase();
      return f.includes("electric") || f.includes("hybrid") || b === "tesla";
    }).sort(byTransparency);
    return padTiles(list);
  }, [vehicles]);

  const submit = async () => {
    if (!user) { window.location.href = "/login?next=/alfa-one-path"; return; }
    if (!user.is_member) { toast.error("Drive Alfa Club membership required to apply."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/business/apply`, form);
      toast.success("Application submitted. Our team will review shortly.");
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit application"); }
    finally { setBusy(false); }
  };

  const applyBtn = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))] shadow-[0_10px_30px_-12px_hsla(220,90%,45%,0.55)]" data-testid="alfa-one-path-submit-button">
          {user?.is_member ? <>Apply for Alfa One Path <ArrowRight className="ml-1 h-4 w-4" /></> : <><Lock className="h-4 w-4 mr-1.5" /> Apply (members only)</>}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Apply for Alfa One Path</DialogTitle>
          <DialogDescription>Tell us about your business and what you need. Our team will follow up.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div><Label>Business name</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Alfa Contracting Ltd." data-testid="op-form-business" /></div>
          <div>
            <Label>Business type</Label>
            <Select value={form.business_type} onValueChange={(v) => setForm({ ...form, business_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sole_prop">Sole proprietor</SelectItem>
                <SelectItem value="corp">Corporation</SelectItem>
                <SelectItem value="registered">Registered business</SelectItem>
                <SelectItem value="rideshare">Rideshare / delivery operator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>What do you plan to use the vehicle for?</Label><Textarea rows={3} value={form.business_purpose} onChange={(e) => setForm({ ...form, business_purpose: e.target.value })} data-testid="op-form-purpose" /></div>
          <div><Label>Vehicle need</Label><Input value={form.vehicle_need} onChange={(e) => setForm({ ...form, vehicle_need: e.target.value })} placeholder="Ford F-150 · Tesla Model 3 · Ram 1500 · Toyota Prius XLE" data-testid="op-form-vehicle" /></div>
          <div>
            <Label>Estimated duration</Label>
            <Select value={form.estimated_duration} onValueChange={(v) => setForm({ ...form, estimated_duration: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1 month">1 month</SelectItem>
                <SelectItem value="3 months">3 months</SelectItem>
                <SelectItem value="6 months">6 months</SelectItem>
                <SelectItem value="12 months">12 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]">
            {busy ? "Submitting…" : "Submit application"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div data-testid="alfa-one-path-page" className="overflow-hidden">
      {/* ============================ HERO ============================ */}
      <section className="relative border-b border-border">
        <div className="absolute inset-0 -z-10 hero-wash" />
        <div aria-hidden className="pointer-events-none absolute -top-24 -right-24 h-[520px] w-[520px] rounded-full glow-blue -z-10" />
        <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-24 h-[420px] w-[420px] rounded-full glow-blue -z-10" />

        <div className="container-x py-16 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] uppercase tracking-widest text-muted-foreground shadow-sm" data-testid="op-hero-badge">
                <Briefcase className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Alfa One Path · Business mobility platform
              </div>
              <h1 className="mt-5 font-display text-5xl sm:text-7xl font-bold leading-[1.02] tracking-tight" data-testid="op-hero-headline">
                Business mobility <span className="text-[hsl(var(--primary))]">that keeps you moving forward.</span>
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed" data-testid="op-hero-subheadline">
                From rideshare-ready vehicles and work trucks to economy cars and electric vehicles, Alfa One Path gives businesses, operators, and entrepreneurs flexible access to the vehicles they need — whenever they need them.
              </p>
              <p className="mt-4 text-sm text-muted-foreground max-w-2xl">
                Flexible terms, 24-hour rental access, long-term business plans, and 24/7/365 support. Early mornings, late nights, emergency replacements, short-term needs, or long-term operational support — AlfaDrive keeps you moving.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild variant="outline" className="h-12 px-5 rounded-xl border-border hover:bg-secondary" data-testid="op-hero-browse-btn">
                  <Link to="/browse?category=truck">Browse business vehicles <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
                {applyBtn}
                {!user?.is_member && (
                  <Button asChild variant="ghost" className="h-12 px-5 rounded-xl text-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-100))]" data-testid="op-hero-join-btn">
                    <Link to="/drive-alfa-club">Join Drive Alfa Club</Link>
                  </Button>
                )}
              </div>

              {/* Live status chip */}
              <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                <div className="inline-flex items-center gap-2"><span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" /></span>Support online · 24/7/365</div>
                <div className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Serving Ontario</div>
                <div className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Insured fleet</div>
              </div>
            </motion.div>

            {/* Right side — floating bento */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                <div aria-hidden className="absolute -inset-6 rounded-[36px] bg-gradient-to-br from-[hsl(var(--blue-100))] to-transparent blur-2xl -z-10" />
                <div className="grid grid-cols-6 gap-3">
                  <div className="col-span-4 rounded-3xl border border-border bg-white p-5 shadow-sm float-slow shine-on-hover">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground"><Clock className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> 24-hour rental</div>
                    <div className="mt-2 font-display text-2xl font-semibold">Any hour. Any day.</div>
                    <div className="mt-1 text-sm text-muted-foreground">Book, extend, and pick up when business needs it.</div>
                  </div>
                  <div className="col-span-2 rounded-3xl border border-border bg-[hsl(var(--charcoal-900))] p-5 text-white shine-on-hover">
                    <LifeBuoy className="h-5 w-5 text-white" />
                    <div className="mt-2 font-display text-xl font-semibold leading-tight">Real people</div>
                    <div className="mt-1 text-xs text-white/70">24/7/365</div>
                  </div>
                  <div className="col-span-3 rounded-3xl border border-border bg-white p-5 shadow-sm shine-on-hover">
                    <Truck className="h-5 w-5 text-[hsl(var(--primary))]" />
                    <div className="mt-2 font-display text-lg font-semibold">Trucks · Sedans · SUVs</div>
                    <div className="mt-1 text-xs text-muted-foreground">Work-ready fleet across Ontario</div>
                  </div>
                  <div className="col-span-3 rounded-3xl border border-border bg-white p-5 shadow-sm shine-on-hover">
                    <Zap className="h-5 w-5 text-[hsl(var(--primary))]" />
                    <div className="mt-2 font-display text-lg font-semibold">EV-ready</div>
                    <div className="mt-1 text-xs text-muted-foreground">Modern electric options for future-focused ops.</div>
                  </div>
                  <div className="col-span-6 rounded-3xl border border-border bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--blue-700))] p-5 text-white shadow-[0_20px_50px_-25px_hsla(220,90%,45%,0.6)]">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-white/80">Business continuity</div>
                        <div className="mt-1 font-display text-2xl font-semibold">Vehicle down? We move fast.</div>
                        <div className="mt-1 text-sm text-white/85">Emergency replacements for teams that cannot slow down.</div>
                      </div>
                      <Rocket className="h-9 w-9 text-white/90" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================ CAROUSEL 1 · TRUCKS ============================ */}
      <section className="section-y bg-white border-b border-border" data-testid="op-carousel-trucks">
        <div className="container-x">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground"><Truck className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Pickup trucks</div>
              <h2 className="mt-2 font-display text-3xl sm:text-5xl font-semibold leading-tight">Pickup Trucks Built for Business</h2>
              <p className="mt-3 text-muted-foreground max-w-xl">From contractors and trades to operators who need capability on demand, Alfa One Path gives business users access to trucks that work as hard as they do.</p>
            </div>
            <Button asChild variant="outline" className="rounded-xl border-border">
              <Link to="/browse?category=truck">Need a truck for work? <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
        <div className="mt-8">
          <Marquee items={trucks} speed="slow" />
        </div>
      </section>

      {/* ============================ VALUE PROP ============================ */}
      <section className="section-y bg-[hsl(var(--charcoal-100))]">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Mobility that works on your schedule</div>
              <h2 className="mt-2 font-display text-3xl sm:text-5xl font-semibold leading-[1.05]">When mobility matters, <span className="text-[hsl(var(--primary))]">AlfaDrive is there.</span></h2>
              <p className="mt-4 text-muted-foreground max-w-lg">Alfa One Path is designed for people and businesses who cannot afford delays. Early mornings, late nights, sudden replacement needs — we give you access to mobility when it matters most.</p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {[
                { i: Clock, t: "24-hour rental access", d: "Pick up and return around your operations, not the other way around." },
                { i: Rocket, t: "Emergency replacement", d: "A company vehicle down? We move quickly to keep you online." },
                { i: CalendarClock, t: "Flexible rental terms", d: "Hourly, daily, weekly, monthly and 3 / 6 / 12-month options." },
                { i: PhoneCall, t: "Fast business support", d: "Priority queue for operators when timelines are tight." },
                { i: Layers, t: "Modern digital booking", d: "Book, extend, and manage from any device." },
                { i: MapPin, t: "Ontario-wide access", d: "The right vehicle available across the province." },
              ].map((c) => (
                <div key={c.t} className="rounded-2xl bg-white border border-border p-5 shine-on-hover">
                  <c.i className="h-5 w-5 text-[hsl(var(--primary))]" />
                  <div className="mt-3 font-display text-lg font-semibold">{c.t}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{c.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================ CAROUSEL 2 · ECONOMY / RIDESHARE ============================ */}
      <section className="section-y bg-white border-b border-border" data-testid="op-carousel-economy">
        <div className="container-x">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground"><Car className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Economy · Rideshare · Delivery</div>
              <h2 className="mt-2 font-display text-3xl sm:text-5xl font-semibold leading-tight">Economy & Rideshare Vehicles</h2>
              <p className="mt-3 text-muted-foreground max-w-xl">Reliable, efficient, and built for daily use — ideal for rideshare operators, delivery drivers, and business users who need practical mobility that performs.</p>
            </div>
            <Button asChild variant="outline" className="rounded-xl border-border">
              <Link to="/browse?category=rideshare">Looking for a rideshare-ready vehicle? <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
        <div className="mt-8">
          <Marquee items={economy} />
        </div>
      </section>

      {/* ============================ TRANSFORMATIVE MESSAGING ============================ */}
      <section className="section-y bg-[hsl(var(--charcoal-900))] text-white relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 -z-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,hsla(220,90%,55%,0.24),transparent_50%),radial-gradient(circle_at_80%_80%,hsla(220,100%,60%,0.16),transparent_55%)]" />
        </div>
        <div className="relative container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-widest text-white/60">When your business needs to move</div>
              <h2 className="mt-2 font-display text-3xl sm:text-5xl font-semibold leading-tight">Alfa One Path <span className="text-[hsl(var(--blue-300))]">moves with it.</span></h2>
              <p className="mt-5 text-white/80 text-lg max-w-2xl leading-relaxed">
                Breakdowns happen. Demand changes. Opportunities come quickly. Alfa One Path is built to help businesses respond without losing momentum. If a company vehicle goes down, if a contractor needs a truck, if a rideshare driver needs a replacement, or if a team needs a practical mobility solution fast — AlfaDrive helps keep your business moving forward.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild className="h-12 px-6 rounded-xl bg-white text-[hsl(var(--charcoal-900))] hover:bg-[hsl(var(--blue-100))]"><Link to="/browse">Explore business-ready options <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
                <span className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white/10 text-xs text-white/80 backdrop-blur"><Timer className="h-3.5 w-3.5" /> Response within minutes</span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { i: HeartHandshake, t: "Always available", d: "Team ready 24/7/365 for business customers." },
                  { i: Timer, t: "Fast response", d: "When your mobility needs change quickly, we work fast." },
                  { i: TrendingUp, t: "Business continuity", d: "Unexpected vehicle issues don’t need to slow operations." },
                  { i: LineChart, t: "Scalable plans", d: "Grow up or down as your operation evolves." },
                ].map((c) => (
                  <div key={c.t} className="rounded-2xl border border-white/15 bg-white/5 backdrop-blur p-5 hover:border-white/30 hover:bg-white/10 transition-colors">
                    <c.i className="h-5 w-5 text-[hsl(var(--blue-300))]" />
                    <div className="mt-3 font-display text-base font-semibold">{c.t}</div>
                    <div className="mt-1 text-xs text-white/70">{c.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ CAROUSEL 3 · ELECTRIC ============================ */}
      <section className="section-y bg-white border-b border-border" data-testid="op-carousel-electric">
        <div className="container-x">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground"><Zap className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Electric vehicles</div>
              <h2 className="mt-2 font-display text-3xl sm:text-5xl font-semibold leading-tight">Electric Vehicles for the Future of Business</h2>
              <p className="mt-3 text-muted-foreground max-w-xl">Modern, efficient, and forward-looking — Alfa One Path offers electric vehicle options for businesses and operators looking to reduce costs and drive the future.</p>
            </div>
            <Button asChild variant="outline" className="rounded-xl border-border">
              <Link to="/browse">Explore electric options <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
        <div className="mt-8">
          <Marquee items={electric} speed="fast" />
        </div>
      </section>

      {/* ============================ BUILT FOR BUSINESS MOBILITY (6-cards, per user copy) ============================ */}
      <section className="section-y bg-[hsl(var(--charcoal-100))]" data-testid="op-benefits-section">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Built for Business Mobility</div>
            <h2 className="mt-2 font-display text-3xl sm:text-5xl font-semibold leading-tight">Whether you drive, manage, or operate — we keep you moving.</h2>
            <p className="mt-3 text-muted-foreground">Alfa One Path helps rideshare drivers, contractors, small businesses, and companies stay mobile with vehicles, plans, and support built around business needs.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { i: Car,          t: "Vehicle Access",       d: "Sedans, SUVs, work trucks, and rideshare-ready vehicles." },
              { i: CalendarClock,t: "Monthly Plans",        d: "Flexible business rental options for operators, teams, and companies." },
              { i: Layers,       t: "3, 6 & 12-Month Terms",d: "Longer-term plans with deposit-based approval." },
              { i: Users2,       t: "Rideshare Ready",      d: "Vehicle options suited for rideshare, delivery, and business driving." },
              { i: Rocket,       t: "Priority Access",      d: "Faster booking support when your business needs a vehicle quickly." },
              { i: HeartHandshake,t: "Business Support",    d: "A dedicated contact for bookings, renewals, and vehicle planning." },
            ].map((c, i) => (
              <motion.div
                key={c.t}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-3xl bg-white border border-border p-6 shine-on-hover hover:border-[hsl(var(--blue-300))] transition-colors"
                data-testid={`op-benefit-${i}`}
              >
                <div className="h-11 w-11 rounded-2xl bg-[hsl(var(--blue-100))] text-[hsl(var(--primary))] flex items-center justify-center">
                  <c.i className="h-5 w-5" />
                </div>
                <div className="mt-4 font-display text-xl font-semibold">{c.t}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.d}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ DYNAMIC PLANS ============================ */}
      <section className="section-y bg-white">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Plans that adapt</div>
              <h2 className="mt-2 font-display text-3xl sm:text-5xl font-semibold leading-tight">No two businesses run the same way.</h2>
              <p className="mt-4 text-muted-foreground max-w-lg">Alfa One Path is designed to support dynamic business vehicle plans, promotions, and pricing structures that can align more closely with your operational needs.</p>
              <div className="mt-6 space-y-2 text-sm text-muted-foreground">
                {["Flexible monthly vehicle plans","Practical long-term mobility options","Tailored solutions for business operators","Scalable vehicle support as you grow","Rental structures built around efficiency"].map((s) => (
                  <div key={s} className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-[hsl(var(--primary))]" /> {s}</div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {[
                { i: Wallet,      t: "Deposit-based approval",   d: "Access longer terms via a straightforward deposit model.", tag: "3 · 6 · 12 mo" },
                { i: Wrench,      t: "Rideshare documentation",  d: "Onboarding help for operator-required paperwork.",         tag: "Operators" },
                { i: Sparkles,    t: "Custom promotions",         d: "Special structures for fleets, teams, and long-term ops.", tag: "Dynamic" },
                { i: ShieldCheck, t: "Confidence built-in",       d: "Insured fleet with 24/7 roadside via our support desk.",   tag: "Peace of mind" },
              ].map((c) => (
                <div key={c.t} className="rounded-2xl border border-border bg-white p-6 shine-on-hover">
                  <div className="flex items-center justify-between">
                    <div className="h-10 w-10 rounded-xl bg-[hsl(var(--blue-100))] text-[hsl(var(--primary))] inline-flex items-center justify-center"><c.i className="h-5 w-5" /></div>
                    <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--primary))] bg-[hsl(var(--blue-100))] px-2 py-0.5 rounded-full">{c.tag}</span>
                  </div>
                  <div className="mt-4 font-display text-lg font-semibold">{c.t}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{c.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================ MEMBERSHIP REQUIRED ============================ */}
      <section className="section-y bg-[hsl(var(--charcoal-100))]">
        <div className="container-x">
          <div className="rounded-[32px] border border-border bg-white p-8 sm:p-10 shadow-[0_30px_80px_-40px_hsla(220,90%,25%,0.25)] relative overflow-hidden" data-testid="op-membership-required">
            <div aria-hidden className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full glow-blue" />
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-[11px] uppercase tracking-widest text-muted-foreground"><KeyRound className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Access</div>
                <h2 className="mt-3 font-display text-3xl sm:text-5xl font-semibold leading-tight">Drive Alfa Club membership required.</h2>
                <p className="mt-3 text-muted-foreground max-w-xl">Access to Alfa One Path is available to eligible Drive Alfa Club members. This ensures business customers receive access to AlfaDrive’s broader premium ecosystem, including member pricing, vehicle access benefits, and advanced mobility options.</p>
              </div>
              <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
                {user?.is_member ? (
                  <>
                    {applyBtn}
                    <Button asChild variant="outline" className="h-12 px-5 rounded-xl border-border"><Link to="/browse">Browse business vehicles</Link></Button>
                  </>
                ) : (
                  <>
                    <Button asChild className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]"><Link to="/drive-alfa-club" data-testid="op-membership-join">Join Drive Alfa Club</Link></Button>
                    <Button asChild variant="outline" className="h-12 px-5 rounded-xl border-border"><Link to="/browse">Browse first</Link></Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ FINAL CTA ============================ */}
      <section className="section-y bg-white">
        <div className="container-x">
          <div className="rounded-[32px] bg-gradient-to-br from-[hsl(var(--charcoal-900))] to-[hsl(var(--blue-700))] p-10 sm:p-14 text-white relative overflow-hidden">
            <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsla(220,100%,70%,0.25),transparent_50%)]" />
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="text-[11px] uppercase tracking-widest text-white/70">Move your business forward</div>
                <h2 className="mt-2 font-display text-3xl sm:text-5xl font-semibold leading-tight">Power your next move with <span className="text-[hsl(var(--blue-300))]">Alfa One Path.</span></h2>
                <p className="mt-4 text-white/85 max-w-2xl">From trucks and economy vehicles to EVs and rideshare-ready options, Alfa One Path gives business users a more flexible, more responsive way to stay moving. Whatever your business needs next, AlfaDrive is ready.</p>
              </div>
              <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
                <Button asChild className="h-12 px-6 rounded-xl bg-white text-[hsl(var(--charcoal-900))] hover:bg-[hsl(var(--blue-100))]" data-testid="op-final-browse"><Link to="/browse">Browse business vehicles</Link></Button>
                {applyBtn}
                {!user?.is_member && (
                  <Button asChild variant="outline" className="h-12 px-5 rounded-xl border-white/40 text-white bg-transparent hover:bg-white/10"><Link to="/drive-alfa-club">Join Drive Alfa Club</Link></Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
